import type { CustomerInfo, CustomerResolveResult } from '@staff-app/shared-types';

/** Badge variant names available in @staff-app/ui Badge. */
export type StatusVariant = 'secondary' | 'warning' | 'destructive' | 'success';

/**
 * Map the (isTrial, isActive) pair to a single account-status label + badge
 * variant. Shared by the customer panel header and the search result card so
 * both read identically.
 */
export function accountStatus(
  isTrial?: boolean,
  isActive?: boolean,
): { label: string; variant: StatusVariant } {
  if (isTrial && isActive) return { label: 'Trial', variant: 'secondary' };
  if (isTrial && !isActive) return { label: 'Trial end', variant: 'warning' };
  if (!isTrial && isActive) return { label: 'Account activated', variant: 'success' };
  return { label: 'Account deactivated', variant: 'destructive' };
}

/**
 * Rank for ordering multi-account search results: paying/active accounts are
 * what a CS agent is almost always looking for, live trials next, then expired
 * trials, dead accounts last. Same (isTrial, isActive) semantics as
 * accountStatus above so the order always matches the badges shown.
 */
export function accountStatusRank(isTrial?: boolean, isActive?: boolean): number {
  if (!isTrial && isActive) return 0; // Account activated
  if (isTrial && isActive) return 1; // Trial
  if (isTrial && !isActive) return 2; // Trial end
  return 3; // Account deactivated
}

/** Status-ordered copy of a result list; ties keep the backend's order (stable sort). */
export function sortByAccountStatus<T extends { isTrial?: boolean; isActive?: boolean }>(
  matches: readonly T[],
): T[] {
  return [...matches].sort(
    (a, b) => accountStatusRank(a.isTrial, a.isActive) - accountStatusRank(b.isTrial, b.isActive),
  );
}

/** Stripe customer id from the company's payment blob, or null. */
export function parseStripeId(info?: CustomerInfo | null): string | null {
  try {
    return info ? ((JSON.parse(info.payment) as Record<string, unknown>)?.stripe_id as string) ?? null : null;
  } catch {
    return null;
  }
}

/** Trial / active flags from the company's payment blob (for the status badge). */
export function parsePaymentFlags(info?: CustomerInfo | null): {
  isTrial?: boolean;
  isActive?: boolean;
} {
  try {
    if (!info) return {};
    const p = JSON.parse(info.payment) as Record<string, unknown>;
    return { isTrial: p?.isTrial as boolean | undefined, isActive: p?.isActive as boolean | undefined };
  } catch {
    return {};
  }
}

/** Licensed screen count (limitation.maxQuantity), or null. */
export function parseMaxScreens(info?: CustomerInfo | null): number | null {
  try {
    return info
      ? ((JSON.parse(info.limitation) as Record<string, unknown>)?.maxQuantity as number) ?? null
      : null;
  } catch {
    return null;
  }
}

/**
 * Account-wide paired screen count — the source of "Paired Screens". It is
 * limitation.quantity (screens paired OUTSIDE every team, the "quantity" field
 * in the panel's info editor) plus `teamPairedScreens` (the regular teams'
 * paired screens). quantity alone read 1 on an account with 145 paired across
 * 12 teams. Excludes on-hold devices. Null when quantity is absent.
 */
export function parsePairedScreens(info?: CustomerInfo | null): number | null {
  try {
    if (!info) return null;
    const quantity = (JSON.parse(info.limitation) as Record<string, unknown>)?.quantity;
    if (typeof quantity !== 'number') return null;
    return quantity + (info.teamPairedScreens ?? 0);
  } catch {
    return null;
  }
}

/** One active add-on: its key + licensed quantity (payment.*.maxQuantity). */
export interface ActiveAddOn {
  key: string;
  /** Licensed quantity for this add-on (maxQuantity); 0 when not set. */
  maxQuantity: number;
}

/**
 * The customer's ACTIVE add-ons (payment.subscriptionIdAddOns where isActive),
 * each with its licensed `maxQuantity`, e.g.
 * [{ key: 'videowall', maxQuantity: 3 }]. Empty when none — callers render
 * nothing in that case.
 */
export function parseActiveAddOns(info?: CustomerInfo | null): ActiveAddOn[] {
  try {
    if (!info) return [];
    const p = JSON.parse(info.payment) as Record<string, unknown>;
    const addOns = Array.isArray(p?.subscriptionIdAddOns) ? p.subscriptionIdAddOns : [];
    return addOns
      .filter(
        (a): a is { key?: string; isActive?: boolean; maxQuantity?: number } =>
          !!a && typeof a === 'object' && (a as { isActive?: boolean }).isActive === true,
      )
      .map((a) => ({
        key: String(a.key ?? '').trim(),
        maxQuantity: Number(a.maxQuantity ?? 0) || 0,
      }))
      .filter((a) => a.key);
  } catch {
    return [];
  }
}

/**
 * The "Searched: <email> [role]" fact, or null when there was no search to report.
 *
 * A real email search always carries `inputRole` (the backend fills it, "Owner" included), so
 * it shows even when the owner was searched. Results synthesized from a recent, a domain match
 * or a device row carry no role and mark themselves `inputIsOwner` — there was no search of
 * that email, so "Searched" would be a restatement of the owner line.
 */
export function searchedFact(
  r: Pick<CustomerResolveResult, 'inputEmail' | 'inputRole' | 'inputIsOwner'>,
): { value: string; role?: string | null } | null {
  if (r.inputRole) return { value: r.inputEmail, role: r.inputRole };
  return r.inputIsOwner ? null : { value: r.inputEmail, role: r.inputRole };
}
